// V-P1 · Universal Search — top-level customer-first search dropdown.
//
// PO-approved contractor experience:
//   Search from the authenticated top-level control
//   → customer-first matching result
//   → related projects grouped beneath that customer
//   → contractor can open either the customer or the appropriate project.
//
// STRICT: this component consumes ONLY the narrow read-only endpoint
// GET /api/search/universal which composes existing customer +
// project data. NO new search architecture. NO new indexing. NO AI
// scoring. NO speculative relevance engine. Existing per-page search
// inputs remain untouched.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, User, Briefcase, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const DEBOUNCE_MS = 220;

export default function UniversalSearch({ className = "", compact = false }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ customers: [], projects: [] });
  const navigate = useNavigate();
  const debRef = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const fetchResults = useCallback(async (query) => {
    const trimmed = (query || "").trim();
    if (!trimmed) {
      setResults({ customers: [], projects: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/search/universal?q=${encodeURIComponent(trimmed)}`);
      setResults(r.data || { customers: [], projects: [] });
    } catch {
      setResults({ customers: [], projects: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!open) return;
    debRef.current = setTimeout(() => fetchResults(q), DEBOUNCE_MS);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, open, fetchResults]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goCustomer = (cid) => {
    setOpen(false);
    setQ("");
    navigate(`/app/customers/${cid}`);
  };
  const goProject = (pid) => {
    setOpen(false);
    setQ("");
    navigate(`/app/projects/${pid}`);
  };

  const hasAny =
    (results.customers?.length || 0) + (results.projects?.length || 0) > 0;
  const showNoResults =
    !loading && q.trim().length > 0 && !hasAny;

  return (
    <div
      ref={boxRef}
      className={`relative ${compact ? "w-full" : "w-full max-w-xl"} ${className}`}
      data-testid="universal-search-root"
    >
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)] pointer-events-none"
        />
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("universal_search.placeholder")}
          data-testid="universal-search-input"
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-[var(--jc-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]/25 focus:border-[var(--jc-blue)]"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setResults({ customers: [], projects: [] });
              inputRef.current?.focus();
            }}
            data-testid="universal-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] p-1 rounded"
            aria-label={t("universal_search.clear")}
          >
            <X size={12} />
          </button>
        )}
      </div>
      {open && (q.trim().length > 0 || loading) && (
        <div
          className="absolute left-0 right-0 mt-1 bg-white border border-[var(--jc-border)] rounded-md shadow-lg z-50 max-h-[70vh] overflow-y-auto"
          data-testid="universal-search-dropdown"
        >
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-[var(--jc-text-soft)]">
              <Loader2 size={12} className="animate-spin" />
              {t("universal_search.searching")}
            </div>
          )}
          {!loading && showNoResults && (
            <div
              className="px-3 py-4 text-sm text-[var(--jc-text-soft)] text-center"
              data-testid="universal-search-no-results"
            >
              {t("universal_search.no_results", q.trim())}
            </div>
          )}
          {!loading && (results.customers || []).map((group) => (
            <div
              key={group.customer.id}
              className="border-b border-[var(--jc-border)] last:border-b-0"
              data-testid={`universal-search-customer-group-${group.customer.id}`}
            >
              <button
                type="button"
                onClick={() => goCustomer(group.customer.id)}
                data-testid={`universal-search-customer-${group.customer.id}`}
                className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-[var(--jc-bg)]"
              >
                <span className="shrink-0 w-8 h-8 rounded-full bg-[var(--jc-blue)]/12 text-[var(--jc-blue)] flex items-center justify-center">
                  <User size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {group.customer.display_name}
                  </div>
                  <div className="text-[11px] text-[var(--jc-text-soft)] truncate">
                    {[group.customer.primary_phone, group.customer.primary_email]
                      .filter(Boolean)
                      .join(" · ") || group.customer.address || t("universal_search.customer_hint")}
                  </div>
                </div>
                <ArrowUpRight size={12} className="text-[var(--jc-blue)] shrink-0" />
              </button>
              {group.projects && group.projects.length > 0 && (
                <ul className="pl-11 pb-2 space-y-0.5">
                  {group.projects.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => goProject(p.id)}
                        data-testid={`universal-search-project-${p.id}`}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--jc-bg)] flex items-center gap-2 text-xs"
                      >
                        <Briefcase size={11} className="text-[var(--jc-text-soft)] shrink-0" />
                        <span className="min-w-0 flex-1 truncate">
                          {p.job_type || p.job_address || p.customer_name || "—"}
                        </span>
                        {p.status && (
                          <span className="text-[10px] text-[var(--jc-text-soft)] uppercase tracking-wider shrink-0">
                            {p.status}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {!loading && (results.projects || []).length > 0 && (
            <div
              className="border-t border-[var(--jc-border)]"
              data-testid="universal-search-ungrouped-projects"
            >
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]">
                {t("universal_search.other_projects")}
              </div>
              <ul>
                {results.projects.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => goProject(p.id)}
                      data-testid={`universal-search-project-only-${p.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--jc-bg)] flex items-center gap-2"
                    >
                      <Briefcase size={12} className="text-[var(--jc-text-soft)]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold truncate">
                          {p.customer_name || p.job_type || "—"}
                        </div>
                        {(p.job_type || p.job_address) && (
                          <div className="text-[11px] text-[var(--jc-text-soft)] truncate">
                            {[p.job_type, p.job_address].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                      <ArrowUpRight size={12} className="text-[var(--jc-blue)] shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
